import * as DTO from '../../models/api/DTO';
import { UserRole } from '../Common';

type Version = '2';

export type ClientSmallBrand = 'rc' | 'om' | 'winc';
export type BrandURLScheme = 'readcloud' | 'omxebooks' | 'winc';
export const clientSmallBrandToSchemeMap: {
  [k in ClientSmallBrand]: BrandURLScheme;
} = {
  om: 'omxebooks',
  rc: 'readcloud',
  winc: 'winc',
};

export const walkthroughBrandMessages: { [k in ClientSmallBrand]: string } = {
  om: 'Welcome to the OfficeMax eBooks reader',
  rc: 'Welcome to the ReadCloud reader',
  winc: 'Welcome to Winc eBooks',
};

export interface ReadStatEvent {
  pageStart: number;
  pageEnd: number;
}

export interface StateBase {
  id?: string;
  created: string;
  updated: string;
  deleted: boolean;
}

export interface BookState extends StateBase {
  title: string;
  downloadURI: string;
  description: string;
  authorName: string;
  imageThumbnailURI: string;
  isbn?: string;
  minor: string;
  longId: string;
  owner: string;
  tags: TagState[];
  type: string;
  publisher?: string;
  createdDateTime: string;
  updatedDateTime: string;
  thumbnailUrls?: string[];
  bookbytesUrls?: string[];
  rcsData?: DTO.RCSBookData;
  options?: {
    disableComments?: boolean;
    printable?: boolean;
    pageCount?: number;
  };
}

export interface UserState {
  access_token: string;
  refresh_token: string;
  module_permissions: string[];
  clientId: string;
  clientBrandName?: string;
  clientVersion?: string;
  clientSmallBrand?: 'rc' | 'om';
  id: string;
  email: string;
  role: UserRole;
  firstName: string;
  lastName: string;
  institution: string;
  thumbnailURI?: string;
}

export interface BookOptions {
  disableComments?: boolean;
  printable?: boolean;
  pageCount?: number;
}

export interface ReaderState {
  title: string;
  bookId: string;
  cloudId: string;
  fileUrl: string;
  minor?: string;
  longId?: string;
  isbn?: string;
  viewState?: ViewState;
  renderServerUrl?: string;
  rcsData?: DTO.RCSBookData;
  bookOptions?: BookOptions;
  version?: string;
}

export const isAnnotationShared = (annotation: {
  visibleInClouds: DTO.Annotation['visibleInClouds'];
  visibleToUsers: DTO.Annotation['visibleToUsers'];
}) => !!(annotation.visibleInClouds.length || annotation.visibleToUsers.length);

export type EpubTheme = DTO.EpubTheme;
export type Annotation = DTO.Annotation;

export type Comment = StateBase & {
  id: string;
  subjectId: string;
  subjectType: 'Comment' | 'Annotation' | 'Book' | 'Unknown';
  text: string;
  version: Version;
  visibleInClouds: string[];
  authorId: string;
  clientCreated?: string;
  clientUpdated?: string;
};

export interface Bookmark extends StateBase {
  id: string;
  bookId: string;
  cloudIds: string[];
  location: string;
  text: string;
  version: Version;
  authorId: string;
  clientCreated?: string;
  clientUpdated?: string;
}

export interface BookUpdatePayload {
  id?: string;
  title: string;
  downloadURI: string;
  description: string;
  authorName: string;
  imageThumbnailURI: string;
  owner: string;
  clouds: string[];
}

export interface BookUploadPayload {
  id: string;
}

export interface BookUploadResponse {
  result: {
    userUploadId: string;
  };
}
export interface BookAddRequest {
  authorName: string;
  authorURI?: string;
  categories?: string[];
  dcExtent?: string;
  dcIssued?: string;
  dcLanguage?: string;
  dcSource?: string;
  description: string;
  downloadURI?: string;
  epubURI?: string;
  imageThumbnailURI?: string;
  imageURI?: string;
  isbn?: string;
  pdfURI?: string;
  published?: string;
  title: string;
  type?: string;
  tagIds?: string[];
  cloudIds?: string[];
  cloudAction?: 'add' | 'remove' | 'replace';
  options: {
    disableComments: boolean;
    printable?: boolean;
    contentServer?: string;
  };
  bookbytesUploadId?: string;
  thumbnailUploadId?: string;
}

export interface BookAddResponse {
  result: {
    id: string;
  };
}
export type ViewState = DTO.ViewState;
export interface TagState {
  id: string;
  owner: string;
  name: string;
  bookIds: string[];
  version: string;
  created: string;
  updated: string;
  deleted: boolean;
}

export interface TagUpdate {
  name: string;
  type: string;
  version: string;
  bookIds: string[];
}

export interface TagUpdateResponse {
  result: {
    id: string;
    status: string;
  };
}

export interface CloudUpdate {
  name: string;
  description: string;
  members: string[];
  admins: string[];
  books: string[];
  addBookAdminOnly: boolean;
  persistent: boolean;
  customBookSortOrder?: boolean;
}

export type PlatformType =
  | 'windows'
  | 'osx'
  | 'chromebook'
  | 'ios'
  | 'android'
  | 'debug'
  | 'web'
  | 'lti';

export declare type Features = FeatureType[];

export declare type FeatureType = 'AnnotationsV3' | 'PublisherAnnotations';
